// web-staking-estimator-frontend/src/components/common/InfoButton.js
import React, { useRef, useState, useEffect } from "react";

/**
 * Custom hook to close popover when clicking outside
 */
function useGlobalClickCloser(cb) {
  const ref = useRef(null);
  useEffect(() => {
    function onDocClick(e) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) cb();
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [cb]);
  return ref;
}

/**
 * Info button with popover tooltip
 * Handles responsive positioning for mobile and desktop
 */
export function InfoButton({ id, label, isOpen, onToggle, children }) {
  const wrapRef = useGlobalClickCloser(() => onToggle(false));
  const btnRef = useRef(null);
  const [popStyle, setPopStyle] = useState(null);

  function open() {
    onToggle(true);
    try {
      const isMobile = window.matchMedia && window.matchMedia("(max-width: 640px)").matches;
      const PAD = 16;
      const MAXW = Math.min(32 * 16, window.innerWidth - PAD * 2);
      const TOP_CLAMP = (btnRect) => Math.min(window.innerHeight - 220, btnRect.bottom + 8);

      if (btnRef.current) {
        const r = btnRef.current.getBoundingClientRect();

        if (isMobile) {
          setPopStyle({
            position: "fixed",
            top: `${TOP_CLAMP(r)}px`,
            left: `${PAD}px`,
            right: `${PAD}px`,
            maxWidth: `calc(100vw - ${PAD * 2}px)`,
            width: "auto",
          });
          return;
        }

        const willOverflowRight = (r.left + MAXW + PAD) > window.innerWidth;
        const willOverflowLeft = r.left < PAD;

        if (willOverflowRight || willOverflowLeft) {
          const left = Math.max(PAD, Math.min(r.left, window.innerWidth - MAXW - PAD));
          setPopStyle({
            position: "fixed",
            top: `${TOP_CLAMP(r)}px`,
            left: `${left}px`,
            width: `${MAXW}px`,
            maxWidth: `${MAXW}px`,
          });
        } else {
          setPopStyle(null);
        }
      } else {
        setPopStyle(null);
      }
    } catch {
      setPopStyle(null);
    }
  }

  return (
    <span className="infobtn-wrap" ref={wrapRef}>
      <button
        ref={btnRef}
        type="button"
        aria-label={`Info: ${label}`}
        className="infobtn"
        onClick={(e) => {
          e.stopPropagation();
          isOpen ? onToggle(false) : open();
        }}
      >
        i
      </button>
      {isOpen && (
        <div className="infobtn-pop" style={popStyle}>
          {children}
        </div>
      )}
    </span>
  );
}
